import {
  useDispatchGetCompatibilityReport,
  useSelectCompatibilityReportGraphs,
} from 'affiny-core/hooks';
import { EGender } from 'affiny-core/modules/account/types';
import { IGraph } from 'affiny-core/modules/api/models/compatibilityReport/compatibilityReport.types';
import { ECompatibilityReportType } from 'affiny-core/modules/api/models/member/member.types';
import { ICompatibilityReportTabProps } from 'affiny-core/modules/compatibilityReport/types';
import { IRootState } from 'affiny-core/modules/state';
import { selectCompatibilityReportAnalyses } from 'affiny-core/redux/selectors/compatReportSelectors';
import { Box, Flex, rem, Text, Title, ValueBar } from 'affiny-ui/src';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { CompatibilityAvatars } from '../CompatibilityAvatars';
import { CompatibilityReportChapterNavigation } from './CompatibilityReportChapterNavigation';
import { CompatibilityReportIntro } from './CompatibilityReportIntro';

interface ICompatibilityReportValuesChapter
  extends ICompatibilityReportTabProps {
  setSection: (arg: string) => void;
  reportType: ECompatibilityReportType;
}

export const CompatibilityReportValuesChapter = ({
  setSection,
  nickname,
  accountId,
  score,
  wantedGender,
  reportType,
}: ICompatibilityReportValuesChapter) => {
  useDispatchGetCompatibilityReport(accountId, reportType);

  const analyses = useSelector((state: IRootState) =>
    selectCompatibilityReportAnalyses(
      accountId,
      ECompatibilityReportType.VALUE
    )(state)
  );

  const otherGraphs = useSelectCompatibilityReportGraphs(
    ECompatibilityReportType.VALUE,
    accountId,
    'other'
  );

  const similarGraphs = useSelectCompatibilityReportGraphs(
    ECompatibilityReportType.VALUE,
    accountId,
    'similar'
  );
  const differentGraphs = useSelectCompatibilityReportGraphs(
    ECompatibilityReportType.VALUE,
    accountId,
    'different'
  );

  const { t } = useTranslation();

  const renderGraph = useCallback(
    (graph: IGraph) => {
      const title = t(`compatibilityReport.value.graphLabels.${graph.id}`);

      return (
        <Flex width="50%" mb="large" pl="large">
          {graph.matrices.map((matrix, idx) => (
            <Box key={`graph.${idx}`} width="100%" maxWidth={rem(320)}>
              <ValueBar
                title={title}
                value={matrix.viewer.x}
                text={t(`compatibilityReport.graphs.other`, {
                  WANTED_GENDER: wantedGender,
                })}
                color="accent"
                containerStyle={{
                  mb: 'medium',
                }}
              />
              <ValueBar
                value={matrix.viewee.x}
                text={t(`compatibilityReport.graphs.me`)}
                color="apricot"
              />
            </Box>
          ))}
        </Flex>
      );
    },
    [t, wantedGender]
  );

  return (
    <Box>
      <Box mb="xLarge">
        {score && nickname && (
          <CompatibilityReportIntro
            reportType={reportType}
            score={score}
            nickname={nickname}
          />
        )}
      </Box>

      {similarGraphs.length > 0 && (
        <SSection>
          <Title mb="large">
            {t(`compatibilityReport.value.similarValues`)}
          </Title>
          <Flex justifyContent="space-between" flexWrap="wrap">
            {similarGraphs.map(graph => renderGraph(graph))}
          </Flex>
        </SSection>
      )}

      {differentGraphs.length > 0 && (
        <SSection>
          <Title mb="large">
            {t(`compatibilityReport.value.differentValues`)}
          </Title>
          <Flex justifyContent="space-between" flexWrap="wrap">
            {differentGraphs.map(graph => renderGraph(graph))}
          </Flex>
        </SSection>
      )}

      {otherGraphs.length > 0 && (
        <SSection>
          <Title mb="large">
            {t(`compatibilityReport.value.valuesSystem`)}
          </Title>
          <Flex justifyContent="space-between" flexWrap="wrap">
            {otherGraphs.map(graph => renderGraph(graph))}
          </Flex>
        </SSection>
      )}

      <Flex alignItems="center" flexDirection="column" mt={rem(56)}>
        {score && (
          <Box zIndex={0}>
            <CompatibilityAvatars
              size={80}
              accountId={accountId}
              score={score}
              gender={wantedGender as EGender}
            />
          </Box>
        )}
        <Title textAlign="center" mt="large">
          {t(`compatibilityReport.value.analysis`)}
        </Title>
        {!!nickname &&
          analyses?.map((analysis, idx) => (
            <Text key={idx} mt="large" fontSize={rem(18)} lineHeight={rem(28)}>
              {analysis.replace(/\[UL_PSEUDO\]/gm, nickname)}
            </Text>
          ))}
      </Flex>

      <CompatibilityReportChapterNavigation
        goToNextChapter={() => setSection('visions')}
        textNextChapter={t('compatibilityReport.tabs.visions')}
      />
    </Box>
  );
};

const SSection = styled(Box).attrs(() => ({
  mb: 'small',
}))``;
